import { REGION_DATA } from '@app/component/region/FindRegionData';
import Utilities from '@util/Utilities';
import React from 'react';
import { HandThumbUpIcon, TagIcon } from '@heroicons/react/20/solid';
import { Tooltip } from 'react-tooltip';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateGroupEvaluationMethod from '@app/model/company_hotel/MarketStateGroupEvaluationMethod';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import {
	useMarketStateFilterInputTools
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import MarketStateForDayPriceParityIndicator
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/parity/MarketStateForDayPriceParityIndicator';
import Currency from '@app/model/_common/Currency';
import { PreferredTier, RatePromotions } from '@app/model/market_state/MarketStateItem';
import {
	buildPromotionTooltipPayload,
	getPromotionIndicatorState,
	promotionAriaLabel,
	PromotionTooltipPayload,
} from './PromotionIndicator.utilities';


const DisplayMarketStateItemPrice: React.FunctionComponent<{
	current: MarketStateStatDateCompetitorSnapshot
	previous: MarketStateStatDateCompetitorSnapshot | undefined
}> = ({ current, previous }) => {
	const { filter } = useMarketStateFilterInputTools();
	const isAnyProvider = filter.provider === undefined;
	const { companyHotel } = useSelectedCompanyHotel();

	let groupCalculationMethod = undefined;
	if(current.isGroup) {
		const method = companyHotel.settings.calendar.marketStateGroupEvaluationMethod;
		switch (method) {
			case MarketStateGroupEvaluationMethod.MIN:
				groupCalculationMethod = 'Min';
				break;
			case MarketStateGroupEvaluationMethod.MAX:
				groupCalculationMethod = 'Max';
				break;
			case MarketStateGroupEvaluationMethod.AVERAGE:
				groupCalculationMethod = 'Avg';
				break;
		}
	}

	const lowestItemForRoom = current.getLowestItemForRoom();

	if (!!lowestItemForRoom && lowestItemForRoom.isAvailable()) return <>
		{!!lowestItemForRoom.lowestItem.region && !filter.region && REGION_DATA[lowestItemForRoom.lowestItem.region].icon}
		<span>
			{Utilities.formatPrice(lowestItemForRoom.lowestItem.item.price)}
			{groupCalculationMethod && <span className="ml-1 text-gray-400">({groupCalculationMethod})</span>}
		</span>
		{!isAnyProvider && <MarketStateForDayPriceParityIndicator current={current} previous={previous} />}
	</>;

	else if (!!lowestItemForRoom && lowestItemForRoom.isLos())
		return <>
			{!!lowestItemForRoom.lowestItem.region && !filter.region && REGION_DATA[lowestItemForRoom.lowestItem.region].icon}
			<span
				className="italic text-gray-400">({Utilities.formatPrice(lowestItemForRoom.lowestItem.item.price)})</span>
			{!isAnyProvider && <MarketStateForDayPriceParityIndicator current={current} previous={previous} />}
		</>;

	else return null;
};

export const PreferredTierBadge: React.FunctionComponent<{
	plus: boolean
}> = ({ plus }) =>
	<span className="relative inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded bg-amber-400">
		<HandThumbUpIcon className="h-2.5 w-2.5 text-white" />
		{plus && <span
			className="absolute -bottom-0.5 -right-0.5 text-[8px] font-extrabold leading-none text-white"
			style={{ textShadow: '0 0 2px rgba(0,0,0,0.35)' }}>+</span>}
	</span>;

export const PromotionIndicator: React.FunctionComponent<{
	promotions?: RatePromotions | null
	preferredTier?: PreferredTier | null
	currency?: Currency
	variant: 'icon' | 'dot'
	tooltipId: string
}> = ({ promotions, preferredTier, currency, variant, tooltipId }) => {
	const indicatorState = getPromotionIndicatorState(promotions, preferredTier);
	if (!indicatorState.visible) return null;

	const formatAmount = currency == null
		? undefined
		: (amount: number) => Utilities.formatAmount(amount, currency);
	const payload = buildPromotionTooltipPayload(promotions, preferredTier, formatAmount);
	const ariaLabel = promotionAriaLabel(payload);
	const serializedPayload = JSON.stringify(payload);

	if (variant === 'dot') {
		return <span
			role="img"
			tabIndex={0}
			aria-label={ariaLabel}
			data-tooltip-id={tooltipId}
			data-promotion-tooltip-payload={serializedPayload}
			className={Utilities.classNames(
				'relative cursor-pointer inline-flex rounded-full w-[8px] h-[8px] shrink-0 ring-1 ring-inset',
				indicatorState.funding === 'hotel' && 'bg-green-500 ring-green-700/20',
				indicatorState.funding === 'booking' && 'bg-blue-500 ring-blue-700/20',
				indicatorState.funding === 'tier' && 'bg-amber-400 ring-amber-600/30',
			)}>
			{indicatorState.mixedFunding && <span
				aria-hidden="true"
				className="absolute -right-0.5 -top-0.5 h-1 w-1 rounded-full bg-blue-600 ring-1 ring-white" />}
		</span>;
	}

	return <span
		role="img"
		tabIndex={0}
		aria-label={ariaLabel}
		data-tooltip-id={tooltipId}
		data-promotion-tooltip-payload={serializedPayload}
		className="cursor-pointer inline-grid grid-cols-[18px_15px] items-center justify-items-center gap-x-1.5">
		<span className="relative inline-flex">
			{indicatorState.hasPromotion && <TagIcon
				className={Utilities.classNames(
					'h-4 w-4',
					indicatorState.hotelFunded ? 'text-green-700' : 'text-blue-600'
				)} />}
			{indicatorState.mixedFunding && <span
				className="absolute -right-0.5 -top-0.5 h-[7px] w-[7px] rounded-full bg-blue-600 ring-1 ring-white" />}
		</span>
		<span className="inline-flex">
			{indicatorState.showTier && <PreferredTierBadge plus={preferredTier === 'PREFERRED_PLUS'} />}
		</span>
	</span>;
};

export const PromotionTooltipCard: React.FunctionComponent<{
	payload: PromotionTooltipPayload
}> = ({ payload }) =>
	<div className="w-56 text-left">
		{payload.combined != null
			? <div className="flex items-baseline justify-between">
				<span className="text-base font-extrabold text-gray-900">{payload.combined}</span>
				{payload.total != null &&
					<span className="text-xs font-semibold tabular-nums text-gray-500">total {payload.total}</span>}
			</div>
			: <p className="text-xs font-semibold text-gray-400">No promotion on this rate</p>}
		{payload.components.length > 0 && <>
			<p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
				Promotions on this rate</p>
			{payload.components.map((component, index) =>
				<div key={`${component.label}-${index}`} className="mt-0.5">
					<div className="grid grid-cols-[1fr_auto_44px] items-baseline gap-x-2 text-xs">
						<span className="inline-flex items-center gap-x-1.5 whitespace-nowrap font-semibold text-gray-800">
							<span className={Utilities.classNames(
								'inline-flex h-2 w-2 shrink-0 rounded-full',
								component.bookingFunded ? 'bg-blue-500' : 'bg-green-600',
							)} />
							{component.label}
						</span>
						<span className="font-bold tabular-nums text-gray-900">{component.percentage}</span>
						<span className="text-right tabular-nums text-gray-500">{component.amount ?? ''}</span>
					</div>
					<p className="ml-3.5 text-[10px] text-gray-400">{component.note}</p>
				</div>)}
		</>}
		{payload.tier != null &&
			<div className="mt-2 flex items-start gap-x-2 border-t border-gray-100 pt-2">
				<span className="mt-0.5"><PreferredTierBadge plus={payload.tier.plus} /></span>
				<span className="text-xs">
					<span className="font-bold text-gray-800">{payload.tier.label} partner</span><br />
					<span className="text-[10px] text-gray-400">Visibility programme — not a discount</span>
				</span>
			</div>}
	</div>;

export const PromotionTooltip: React.FunctionComponent<{ id: string }> = ({ id }) =>
	<Tooltip
		id={id}
		place="top"
		noArrow
		className="!bg-white shadow ring-1 !ring-black !ring-opacity-5 !rounded-md !text-left !px-3 !py-2 !opacity-100 !z-50"
		render={({ activeAnchor }) => {
			const raw = activeAnchor?.getAttribute('data-promotion-tooltip-payload');
			if (raw == null || raw.length === 0) return null;
			let payload: PromotionTooltipPayload;
			try {
				payload = JSON.parse(raw);
			} catch {
				return null;
			}
			return <PromotionTooltipCard payload={payload} />;
		}}
	/>;

export  default DisplayMarketStateItemPrice;
